/**
 * Hook for collecting process list data from a sandbox.
 *
 * **Primary:** Opens a WebSocket to `/sandboxes/{id}/processes/stream` which
 * streams raw `top -b` output from the VM rootfs (via nsenter into the
 * container's PID namespace). Does NOT require any tools in the container.
 *
 * **Fallback:** If the streaming endpoint is unavailable (older clusters),
 * falls back to the exec-stream approach: opens a bash session and runs
 * `ps aux` in a loop. This requires `ps` to be installed in the container.
 *
 * The fallback is a one-way latch — once the streaming endpoint fails, we
 * stay on exec-stream for the lifetime of the hook to avoid retry storms.
 *
 * @module components/terminal/useProcessList
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useExecStream, type ConnectionState } from '@/hooks';
import { config } from '@/config';

export type { ConnectionState } from '@/hooks';

export interface ProcessEntry {
  pid: number;
  user: string;
  cpu: number;
  mem: number;
  vsz: number;
  rss: number;
  tty: string;
  stat: string;
  start: string;
  time: string;
  command: string;
}

export interface UseProcessListReturn {
  connectionState: ConnectionState;
  processes: ProcessEntry[];
  error: string | null;
  connect: () => void;
  disconnect: () => void;
  retry: () => void;
}

// ---------------------------------------------------------------------------
// Streaming endpoint parser (top -b output)
// ---------------------------------------------------------------------------

/**
 * Parse a single `top -b` batch into ProcessEntry[].
 *
 * top -b output columns:
 *   PID USER PR NI VIRT RES SHR S %CPU %MEM TIME+ COMMAND
 */
function parseTopBatch(raw: string): ProcessEntry[] {
  const lines = raw.split('\n');
  const entries: ProcessEntry[] = [];

  let headerIdx = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*PID\s+USER/.test(lines[i])) {
      headerIdx = i;
      break;
    }
  }

  if (headerIdx === -1) return entries;

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const parts = line.split(/\s+/);
    if (parts.length < 12) continue;

    const pid = parseInt(parts[0], 10);
    if (isNaN(pid)) continue;

    const parseMemField = (s: string): number => {
      const lower = s.toLowerCase();
      const num = parseFloat(lower);
      if (isNaN(num)) return 0;
      if (lower.endsWith('t')) return num * 1024 * 1024 * 1024;
      if (lower.endsWith('g')) return num * 1024 * 1024;
      if (lower.endsWith('m')) return num * 1024;
      return num;
    };

    entries.push({
      pid,
      user: parts[1],
      cpu: parseFloat(parts[8]) || 0,
      mem: parseFloat(parts[9]) || 0,
      vsz: parseMemField(parts[4]),
      rss: parseMemField(parts[5]),
      tty: '',
      stat: parts[7],
      start: '',
      time: parts[10],
      command: parts.slice(11).join(' '),
    });
  }

  return entries;
}

// ---------------------------------------------------------------------------
// Exec-stream fallback parser (ps aux output)
// ---------------------------------------------------------------------------

// Shell script that collects process list as JSON via exec-stream.
// Sent via stdin to a /bin/bash session.
const PROCESS_SCRIPT = `
while true; do
  echo "---PROCS_JSON---"
  ps aux --sort=-%cpu 2>/dev/null | awk 'NR>1 {
    cmd=""
    for(i=11;i<=NF;i++) { cmd = cmd (i==11?"":" ") $i }
    gsub(/\\\\/, "\\\\\\\\", cmd)
    gsub(/"/, "\\\\\\"", cmd)
    gsub(/\\t/, "\\\\t", cmd)
    gsub(/\\r/, "", cmd)
    printf "%s{\\"pid\\":%s,\\"user\\":\\"%s\\",\\"cpu\\":%s,\\"mem\\":%s,\\"vsz\\":%s,\\"rss\\":%s,\\"tty\\":\\"%s\\",\\"stat\\":\\"%s\\",\\"start\\":\\"%s\\",\\"time\\":\\"%s\\",\\"command\\":\\"%s\\"}",
      (NR==2?"":","), $2, $1, $3, $4, $5, $6, $7, $8, $9, $10, cmd
  }'
  echo ""
  echo "---PROCS_END---"
  sleep 2
done
`;

function parseExecStreamBuffer(buffer: string): {
  entries: ProcessEntry[] | null;
  remaining: string;
} {
  const startIdx = buffer.indexOf('---PROCS_JSON---');
  const endIdx = buffer.indexOf('---PROCS_END---');

  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    const jsonStr = buffer.substring(startIdx + 16, endIdx).trim();
    const remaining = buffer.substring(endIdx + 15);
    try {
      const parsed = JSON.parse(`[${jsonStr}]`) as ProcessEntry[];
      return { entries: parsed, remaining };
    } catch {
      return { entries: null, remaining };
    }
  }

  return { entries: null, remaining: buffer };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useProcessList(
  sandboxId: string,
  containerName?: string,
  sandboxGroupId?: string
): UseProcessListReturn {
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [processes, setProcesses] = useState<ProcessEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  // One-way latch: once streaming fails, we stay on exec-stream
  const useExecFallbackRef = useRef(false);
  const wsRef = useRef<WebSocket | null>(null);
  const hasConnectedRef = useRef(false);

  // --- Exec-stream fallback state ---
  const [execCommandSent, setExecCommandSent] = useState(false);
  const execBufferRef = useRef<string>('');
  const execDisconnectRef = useRef<(() => void) | null>(null);
  const execConnectRef = useRef<((height?: number, width?: number) => void) | null>(null);
  const execSendInputRef = useRef<((data: string) => void) | null>(null);
  const execActiveRef = useRef(false);

  const handleExecOutput = useCallback((data: string) => {
    execBufferRef.current += data;
    if (execBufferRef.current.length > 200000) {
      execBufferRef.current = execBufferRef.current.slice(-100000);
    }
    const { entries, remaining } = parseExecStreamBuffer(execBufferRef.current);
    execBufferRef.current = remaining;
    if (entries && entries.length > 0) {
      setProcesses(entries);
      setError(null);
    }
  }, []);

  const handleExecExit = useCallback((exitCode: number) => {
    if (exitCode !== 0) {
      setError('Process monitor exited unexpectedly');
    }
  }, []);

  const handleExecError = useCallback((err: string) => {
    setError(`Connection error: ${err}`);
  }, []);

  const handleExecStateChange = useCallback((state: ConnectionState) => {
    if (execActiveRef.current) {
      setConnectionState(state);
      if (state === 'connected') setError(null);
    }
  }, []);

  const execStream = useExecStream({
    sandboxId,
    sandboxGroupId,
    containerName,
    command: '/bin/bash',
    environment: { HISTFILE: '/dev/null' },
    onOutput: handleExecOutput,
    onExit: handleExecExit,
    onError: handleExecError,
    onStateChange: handleExecStateChange,
    autoReconnect: false,
  });

  useEffect(() => {
    execDisconnectRef.current = execStream.disconnect;
    execConnectRef.current = execStream.connect;
    execSendInputRef.current = execStream.sendInput;
  }, [execStream.disconnect, execStream.connect, execStream.sendInput]);

  // Send ps aux script when exec-stream connects
  useEffect(() => {
    if (execActiveRef.current && connectionState === 'connected' && !execCommandSent) {
      const timer = setTimeout(() => {
        execSendInputRef.current?.(PROCESS_SCRIPT + '\n');
        setExecCommandSent(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [connectionState, execCommandSent]);

  // --- Start exec-stream fallback ---
  const startExecFallback = useCallback(() => {
    console.log('[ProcessList] Mode: exec-stream (ps aux fallback)');
    execActiveRef.current = true;
    execBufferRef.current = '';
    setExecCommandSent(false);
    setConnectionState('connecting');
    setError(null);
    execConnectRef.current?.(24, 120);
  }, []);

  // --- Primary: streaming WebSocket ---
  const connectStreaming = useCallback(() => {
    console.log('[ProcessList] Mode: streaming WebSocket (/processes/stream)');
    const apiUrl = config.apiBaseUrl;
    const wsScheme = apiUrl.startsWith('https://') ? 'wss' : 'ws';
    const host = apiUrl.replace(/^https?:\/\//, '');
    // Auth is handled via HttpOnly cookie - browser sends it during WebSocket handshake
    const wsUrl = `${wsScheme}://${host}/${sandboxGroupId ? `sandboxGroups/${encodeURIComponent(sandboxGroupId)}/sandboxes` : 'sandboxes'}/${encodeURIComponent(sandboxId)}/processes/stream${containerName ? `?containerName=${encodeURIComponent(containerName)}` : ''}`;

    setConnectionState('connecting');
    setError(null);

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    let receivedData = false;

    ws.onopen = () => {
      setConnectionState('connected');
      setError(null);
    };

    ws.onmessage = (event: MessageEvent) => {
      const data = typeof event.data === 'string' ? event.data : '';
      if (!data) return;
      receivedData = true;

      const parsed = parseTopBatch(data);
      if (parsed.length > 0) {
        setProcesses(parsed);
        setError(null);
      }
    };

    ws.onerror = () => {
      // Streaming endpoint not available — switch to fallback
      if (!receivedData && hasConnectedRef.current) {
        console.warn(
          '[ProcessList] Streaming WebSocket error before receiving data, falling back to exec-stream'
        );
        useExecFallbackRef.current = true;
        wsRef.current = null;
        startExecFallback();
      }
    };

    ws.onclose = (event) => {
      if (!hasConnectedRef.current) return;

      // If we never received data and got a non-normal close, fall back.
      // Status 1006 = abnormal closure (connection refused / 404),
      // status 0 = never opened.
      if (!receivedData && (event.code === 1006 || event.code === 0)) {
        console.warn(
          `[ProcessList] Streaming WebSocket closed with code ${event.code} (${event.reason || 'no reason'}), ` +
            'endpoint likely unavailable on this cluster, falling back to exec-stream'
        );
        useExecFallbackRef.current = true;
        wsRef.current = null;
        startExecFallback();
        return;
      }

      // Normal unexpected close while streaming was working
      setConnectionState('disconnected');
    };
  }, [sandboxId, sandboxGroupId, containerName, startExecFallback]);

  // --- Public API ---
  const connect = useCallback(() => {
    if (hasConnectedRef.current) return;
    hasConnectedRef.current = true;

    if (useExecFallbackRef.current) {
      startExecFallback();
    } else {
      connectStreaming();
    }
  }, [connectStreaming, startExecFallback]);

  const disconnect = useCallback(() => {
    hasConnectedRef.current = false;
    execActiveRef.current = false;

    // Close streaming WS
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    // Close exec-stream
    execDisconnectRef.current?.();
    execBufferRef.current = '';
    setExecCommandSent(false);

    setConnectionState('disconnected');
    setProcesses([]);
    setError(null);
  }, []);

  const retry = useCallback(() => {
    disconnect();
    setTimeout(() => connect(), 100);
  }, [disconnect, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      execDisconnectRef.current?.();
    };
  }, []);

  return {
    connectionState,
    processes,
    error,
    connect,
    disconnect,
    retry,
  };
}
